/**
 * 🟥 CUADRO NEGRO V3 — ProjectGitHubImportPrompt
 *
 * Mensaje del asistente que aparece cuando el usuario manda una URL de GitHub.
 * Botones Importar/Cancelar. Importar llama a POST /api/projects/importFromGitHub
 * y abre el módulo creado en el Workspace.
 */
import { useState } from 'react';
import { Github, Check, X, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const ProjectGitHubImportPrompt = ({
  offer,
  onAccept,
  onDecline,
  initialStatus = 'idle',
}) => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(initialStatus);

  const isEs = (language || 'es').toLowerCase().startsWith('es');
  if (!offer) return null;

  const handleAccept = async () => {
    if (status === 'building' || status === 'accepted') return;
    setStatus('building');
    try {
      const ok = await onAccept?.(offer);
      setStatus(ok ? 'accepted' : 'idle');
    } catch {
      setStatus('idle');
    }
  };

  const handleDecline = () => {
    if (status === 'building' || status === 'accepted') return;
    setStatus('declined');
    onDecline?.();
  };

  return (
    <div
      className="mt-1 rounded-xl border border-violet-500/40 bg-violet-950/30 light:bg-violet-50 light:border-violet-200 p-4 sm:p-5"
      data-testid="project-github-import-prompt"
    >
      <div className="flex items-start gap-3">
        <Github className="w-5 h-5 text-violet-400 light:text-violet-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-violet-300 light:text-violet-800 mb-1">
            {isEs ? 'Detecté un repositorio de GitHub' : 'I detected a GitHub repository'}
          </h4>
          <p className="text-sm text-zinc-300 light:text-zinc-700 leading-relaxed">
            {isEs
              ? '¿Deseas importar este proyecto desde GitHub y convertirlo en un módulo del Área de Trabajo?'
              : 'Do you want me to import this project from GitHub and convert it into a Workspace module?'}
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 light:text-violet-700 font-mono">
              {offer.owner}/{offer.repo}
            </span>
            {offer.branch && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-300 light:bg-zinc-200 light:text-zinc-700">
                {isEs ? 'rama' : 'branch'}: {offer.branch}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button
              onClick={handleAccept}
              disabled={status === 'building' || status === 'accepted' || status === 'declined'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="project-github-import-accept-btn"
            >
              {status === 'building' ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {isEs ? 'Importando…' : 'Importing…'}
                </>
              ) : status === 'accepted' ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  {isEs ? 'Listo en el Área de Trabajo' : 'Ready in Workspace'}
                </>
              ) : (
                <>
                  <Github className="w-3.5 h-3.5" />
                  {isEs ? 'Importar' : 'Import'}
                </>
              )}
            </button>
            <button
              onClick={handleDecline}
              disabled={status === 'building' || status === 'accepted' || status === 'declined'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-zinc-700/60 hover:bg-zinc-700 text-zinc-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="project-github-import-decline-btn"
            >
              <X className="w-3.5 h-3.5" />
              {status === 'declined'
                ? (isEs ? 'Cancelado' : 'Cancelled')
                : (isEs ? 'Cancelar' : 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectGitHubImportPrompt;
